import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';

/**
 * Confirmation screen for approving a repair cost from an emailed link.
 *
 * The link in the email lands here — it does not approve anything on its own.
 * Mail security scanners prefetch links, so an approval that happened on page
 * load would be triggered by the scanner before a person ever saw it. Nothing
 * is recorded until the button below is pressed, which issues a POST.
 *
 * Reachable without signing in: the signed token in the URL is the credential.
 */
const CostApproval = () => {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading' });
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cost-approval/${token}`);
      setState({ status: 'ready', data });
    } catch (err) {
      const d = err.response?.data;
      setState({
        status: 'error',
        message: d?.message || 'This approval link could not be opened.',
        reason: d?.reason,
      });
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/cost-approval/${token}`, { note });
      setState({ status: 'done', data });
    } catch (err) {
      const d = err.response?.data;
      setState({
        status: 'error',
        message: d?.message || 'Could not record your decision. Please try in Tychi Desk.',
        reason: d?.reason,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children) => (
    <div style={{
      minHeight: '100vh', background: 'var(--bg, #f5f6f8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div className="card" style={{ maxWidth: 520, width: '100%', padding: 28 }}>
        {children}
      </div>
    </div>
  );

  if (state.status === 'loading') {
    return shell(<div style={{ textAlign: 'center', color: 'var(--text3)' }}>Loading approval request…</div>);
  }

  if (state.status === 'error') {
    return shell(
      <>
        <h1 style={{ fontSize: 18, margin: '0 0 10px' }}>
          {state.reason === 'decided' ? 'Already decided' : 'Link not usable'}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>{state.message}</p>
        <a className="btn btn-primary" href="/all-tickets" style={{ marginTop: 16, display: 'inline-block' }}>
          Open Tychi Desk
        </a>
      </>
    );
  }

  if (state.status === 'done') {
    const approved = state.data?.action === 'approved';
    return shell(
      <>
        <div style={{ fontSize: 34, marginBottom: 8 }}>{approved ? '✅' : '⛔'}</div>
        <h1 style={{ fontSize: 18, margin: '0 0 10px' }}>
          {approved ? 'Cost approved' : 'Cost not approved'}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>{state.data?.message}</p>
        <p style={{ fontSize: 12, color: 'var(--text3)' }}>
          Recorded against {state.data?.ticket_no}. You can close this page.
        </p>
      </>
    );
  }

  const d = state.data || {};
  const approving = d.action === 'approved';
  const row = (label, value) => (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', fontSize: 13 }}>
      <div style={{ width: 110, color: 'var(--text3)', flexShrink: 0 }}>{label}</div>
      <div style={{ color: 'var(--text)', fontWeight: 500 }}>{value || '—'}</div>
    </div>
  );

  return shell(
    <>
      <h1 style={{ fontSize: 18, margin: '0 0 4px' }}>
        {approving ? 'Approve this repair cost?' : 'Confirm: do not approve?'}
      </h1>
      <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 16px' }}>
        Nothing has been recorded yet. Review the details, then confirm below.
      </p>

      <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
        {row('Ticket', d.ticket_no)}
        {row('Issue', d.subject)}
        {row('Asset', d.asset)}
        {row('Raised by', d.raised_by)}
      </div>

      {d.estimate_notes && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>IT's estimate</div>
          <div style={{
            fontSize: 13, whiteSpace: 'pre-wrap', background: 'var(--bg2)',
            borderRadius: 8, padding: '10px 12px', color: 'var(--text2)',
          }}>
            {d.estimate_notes}
          </div>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="cost-note" style={{ fontSize: 13 }}>
          Note {approving ? '(optional)' : '— please say why'}
        </label>
        <textarea
          id="cost-note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={approving ? 'Anything IT should know…' : 'Reason for not approving…'}
        />
      </div>

      <button
        type="button"
        className="btn btn-primary"
        onClick={submit}
        disabled={submitting || (!approving && !note.trim())}
        style={{ width: '100%', background: approving ? 'var(--green, #1e8e3e)' : 'var(--red, #c00000)' }}
      >
        {submitting
          ? 'Recording…'
          : approving ? 'Confirm approval' : 'Confirm — do not approve'}
      </button>

      {!approving && !note.trim() && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8, textAlign: 'center' }}>
          A reason is required so IT knows what to do next.
        </div>
      )}
    </>
  );
};

export default CostApproval;
